/**
 * features/parent/AddChildCard.tsx
 * ------------------------------------------------------------------
 * Lets an already-signed-in parent add a child by entering that child's
 * DETAILS (name, date of birth, class) — no registration code. Calls the
 * requestChildLink Cloud Function which:
 *   • auto-links on a single confident match (name + DOB + class), then
 *     force-refreshes the auth token so the new child's data loads; or
 *   • defers ambiguous / partial / multiple-match cases to the school
 *     office for approval (a pending link request), returning 'pending'.
 *
 * The reply never reveals whether a matching pupil exists — a deferred
 * request always reads the same "sent to the office" message.
 * ------------------------------------------------------------------ */
import { useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { UserPlus, Loader2, CheckCircle2, Info, AlertCircle, MessageSquare, Clock } from 'lucide-react';
import { functions, auth } from '@/lib/firebase';
import { useData } from '@/contexts/DataContext';
import { SoftCard } from '@/features/finance/ui';
import { PARENT_COMPOSE_PREFILL_KEY } from '@/features/communications/ParentMessagesHub';

type Result = { status: string; studentName?: string; className?: string };

export default function AddChildCard() {
  const { classes } = useData();
  const [open, setOpen] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [surname, setSurname] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [classId, setClassId] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ tone: 'ok' | 'info' | 'pending' | 'error'; text: string } | null>(null);
  const [rejected, setRejected] = useState(false);

  const classOptions = useMemo(
    () => classes.filter((c) => !c.archived),
    [classes],
  );

  const canSubmit = firstName.trim().length >= 2 && surname.trim().length >= 2 && !!dateOfBirth && !!classId && !busy;

  function reset() {
    setFirstName(''); setSurname(''); setDateOfBirth(''); setClassId('');
  }

  async function submit() {
    if (!canSubmit) return;
    setBusy(true); setMsg(null); setRejected(false);
    try {
      const cls = classOptions.find((c) => c.id === classId);
      const fn = httpsCallable<
        { firstName: string; surname: string; dateOfBirth: string; classId: string; className: string },
        Result
      >(functions, 'requestChildLink');
      const { data } = await fn({
        firstName: firstName.trim(),
        surname: surname.trim(),
        dateOfBirth,
        classId,
        className: cls?.name ?? '',
      });
      if (data.status === 'added') {
        await auth.currentUser?.getIdToken(true); // load the new child's rule-gated data now
        setMsg({ tone: 'ok', text: `${data.studentName || 'Your child'} has been added to your portal.` });
        reset();
      } else if (data.status === 'already-linked') {
        setMsg({ tone: 'info', text: `${data.studentName || 'That child'} is already in your portal.` });
        reset();
      } else {
        // pending — deferred to the office
        setMsg({
          tone: 'pending',
          text: "We've sent your request to the school office to verify. You'll get access to this child once they approve it — no code needed.",
        });
        reset();
      }
    } catch {
      setMsg({ tone: 'error', text: 'We could not process that request. Please check the details, or message the school below.' });
      setRejected(true);
    } finally {
      setBusy(false);
    }
  }

  function messageSchool() {
    sessionStorage.setItem(
      PARENT_COMPOSE_PREFILL_KEY,
      JSON.stringify({
        category: 'onboarding',
        subject: 'Trouble adding a child',
        body:
          `I tried to add my child (${firstName.trim()} ${surname.trim()}) to my portal, ` +
          `but ran into a problem. Could you please help link them to my account?`,
      }),
    );
    window.location.hash = '#/parentMessages';
  }

  const fieldCls =
    'w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white';

  return (
    <SoftCard hover={false}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <UserPlus size={18} />
          </div>
          <div>
            <p className="font-semibold text-gray-900 dark:text-white text-sm">Add another child</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Enter your child's details and we'll link them to this account — no code needed.
            </p>
          </div>
        </div>
        {!open && (
          <button
            onClick={() => { setOpen(true); setMsg(null); setRejected(false); }}
            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-primary text-white text-sm font-semibold hover:bg-primaryDark"
          >
            <UserPlus size={15} /> Add child
          </button>
        )}
      </div>

      {open && (
        <div className="mt-4 grid gap-2.5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            <div>
              <label className="text-xs font-medium text-gray-500">Child's first name</label>
              <input className={fieldCls} value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="First name" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Child's surname</label>
              <input className={fieldCls} value={surname} onChange={(e) => setSurname(e.target.value)} placeholder="Surname" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Date of birth</label>
              <input type="date" className={fieldCls} value={dateOfBirth} onChange={(e) => setDateOfBirth(e.target.value)} />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Class</label>
              <select className={fieldCls} value={classId} onChange={(e) => setClassId(e.target.value)}>
                <option value="">— select class —</option>
                {classOptions.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          </div>
          <div className="flex justify-end">
            <button
              onClick={submit}
              disabled={!canSubmit}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-primary text-white text-sm font-semibold hover:bg-primaryDark disabled:opacity-40"
            >
              {busy ? <Loader2 size={15} className="animate-spin" /> : <UserPlus size={15} />}
              Add child
            </button>
          </div>
        </div>
      )}

      {msg && (
        <div
          className={`mt-3 flex items-start gap-2 text-sm rounded-lg px-3 py-2 ${
            msg.tone === 'ok'
              ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
              : msg.tone === 'error'
                ? 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                : msg.tone === 'pending'
                  ? 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                  : 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
          }`}
        >
          {msg.tone === 'ok' ? <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
            : msg.tone === 'error' ? <AlertCircle size={16} className="mt-0.5 shrink-0" />
              : msg.tone === 'pending' ? <Clock size={16} className="mt-0.5 shrink-0" />
                : <Info size={16} className="mt-0.5 shrink-0" />}
          <span>{msg.text}</span>
        </div>
      )}

      {rejected && (
        <button
          onClick={messageSchool}
          className="mt-2 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-primary/30 text-primary text-sm font-semibold hover:bg-primary/5"
        >
          <MessageSquare size={15} /> Message the school about this
        </button>
      )}
    </SoftCard>
  );
}
